import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RegisterRequest } from '../types/auth';
import Button from './ui/Button';

interface UserCreateModalProps {
  onClose: () => void;
  onSave: (userData: RegisterRequest) => void;
  loading: boolean;
}

const UserCreateModal: React.FC<UserCreateModalProps> = ({ onClose, onSave, loading }) => {
  const [formData, setFormData] = useState<RegisterRequest>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'patient',
    specialty: '',
    license_number: '',
    phone: '',
    date_of_birth: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    
    // Role-specific validation (specialty, license are optional)
    
    if (formData.role === 'patient') {
      if (!formData.phone) newErrors.phone = 'Phone is required for patients';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clean up form data based on role
    const cleanedData = { ...formData };
    if (formData.role !== 'member') {
      delete cleanedData.specialty;
      delete cleanedData.license_number;
    }
    if (formData.role !== 'patient') {
      delete cleanedData.phone;
      delete cleanedData.date_of_birth;
    }

    setErrors({});
    onSave(cleanedData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-clinic-dark/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 w-full max-w-2xl shadow-clinic-lg rounded-card bg-white border border-clinic-primary-lighter">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-clinic-dark font-body">Create New User</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 -m-2 rounded-lg text-clinic-muted hover:text-clinic-dark hover:bg-clinic-surface-sunken transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ${
                  errors.first_name ? 'border-clinic-accent' : 'border-clinic-primary-light'
                }`}
                disabled={loading}
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-clinic-accent">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ${
                  errors.last_name ? 'border-clinic-accent' : 'border-clinic-primary-light'
                }`}
                disabled={loading}
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-clinic-accent">{errors.last_name}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ${
                errors.email ? 'border-clinic-accent' : 'border-clinic-primary-light'
              }`}
              disabled={loading}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-clinic-accent">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">
              Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ${
                errors.password ? 'border-clinic-accent' : 'border-clinic-primary-light'
              }`}
              disabled={loading}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-clinic-accent">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">
              Role *
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-clinic-primary-light rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
              disabled={loading}
            >
              <option value="patient">Client</option>
              <option value="member">Member (Clinician)</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Role-specific fields */}
          {formData.role === 'member' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1">
                  Professional Title
                </label>
                <input
                  type="text"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  placeholder="e.g., Cardiology, Dermatology"
                  className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ${
                    errors.specialty ? 'border-clinic-accent' : 'border-clinic-primary-light'
                  }`}
                  disabled={loading}
                />
                {errors.specialty && (
                  <p className="mt-1 text-sm text-clinic-accent">{errors.specialty}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1">
                  License Number
                </label>
                <input
                  type="text"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-clinic-primary-light rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {formData.role === 'patient' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ${
                    errors.phone ? 'border-clinic-accent' : 'border-clinic-primary-light'
                  }`}
                  disabled={loading}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-clinic-accent">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-clinic-dark mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-clinic-primary-light rounded-input focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
              Create User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreateModal;